import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { fetchPagesIndex, resetPage, deletePage } from "@/lib/sacred-pages/sacred-pages-api";
import { PAGE_SLUGS, getDefaultSacredPage } from "@/lib/sacred-pages/defaults";
import { useSacredAdmin } from "@/hooks/useSacredAdmin";
import { Pencil, Plus, Trash2, RotateCcw, Loader2 } from "lucide-react";
import { savePage } from "@/lib/sacred-pages/sacred-pages-api";

export function PagesAdmin() {
  const { canEditPages, isAdmin } = useSacredAdmin();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newSlug, setNewSlug] = useState("");
  const [newTitle, setNewTitle] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const remote = await fetchPagesIndex();
      const known = new Map(PAGE_SLUGS.map((s) => [s, getDefaultSacredPage(s).title]));
      remote.forEach((p) => known.set(p.slug, p.title));
      setPages([...known.entries()].map(([slug, title]) => ({ slug, title })).sort((a, b) => a.slug.localeCompare(b.slug)));
    } catch {
      setPages(PAGE_SLUGS.map((slug) => ({ slug, title: getDefaultSacredPage(slug).title })));
    }
    setLoading(false);
  };

  useEffect(() => {
    if (canEditPages) load();
  }, [canEditPages]);

  const createPage = async () => {
    const slug = newSlug.trim().toLowerCase().replace(/[^a-z0-9-]/g, "-");
    if (!slug) return;
    const page = getDefaultSacredPage(slug);
    page.title = newTitle || page.title;
    await savePage(slug, { page, draft_only: true });
    setNewSlug("");
    setNewTitle("");
    load();
  };

  if (!canEditPages) {
    return <p className="text-sm text-muted-foreground">Pages CMS requires admin, editor, or staff role.</p>;
  }

  if (loading) return <Loader2 className="w-6 h-6 animate-spin" />;

  return (
    <div className="space-y-6" data-testid="pages-admin">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Add new page</CardTitle>
        </CardHeader>
        <CardContent className="grid sm:grid-cols-3 gap-4 items-end">
          <div>
            <Label>Slug</Label>
            <Input placeholder="about-us" value={newSlug} onChange={(e) => setNewSlug(e.target.value)} />
          </div>
          <div>
            <Label>Title</Label>
            <Input placeholder="About Us" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} />
          </div>
          <Button type="button" onClick={createPage}>
            <Plus className="w-4 h-4 mr-1" /> Create
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">All pages ({pages.length})</CardTitle>
          <p className="text-xs text-muted-foreground">Edit JSON sections, hero image, publish or submit for approval.</p>
        </CardHeader>
        <CardContent className="space-y-2">
          {pages.map((p) => (
            <div key={p.slug} className="flex flex-wrap items-center gap-2 p-3 border rounded-lg text-sm">
              <div className="flex-1 min-w-[180px]">
                <p className="font-medium">{p.title}</p>
                <p className="text-muted-foreground text-xs">{p.slug}</p>
              </div>
              <Button size="sm" asChild>
                <Link to={`/admin/pages/${p.slug}/edit`}>
                  <Pencil className="w-4 h-4 mr-1" /> Edit
                </Link>
              </Button>
              {isAdmin && (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => resetPage(p.slug).then(load)}
                    title="Remove KV override (revert to static default)"
                  >
                    <RotateCcw className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => {
                      if (window.confirm(`Delete CMS data for ${p.slug}?`)) deletePage(p.slug).then(load);
                    }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </>
              )}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
